import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Breadcrumb, Button, Card, DatePicker, Descriptions, Drawer, Empty, Input, message, Select, Space, Table, Tag, Typography,
} from "antd";
import { EyeOutlined, HomeOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import service from "../../api/FetchInterceptor";
import { formatMoney, PROVIDER_LABELS } from "../../lib/money";

const STATUSES = {
    paid: { label: "Payée", color: "green" },
    pending: { label: "En attente", color: "orange" },
    failed: { label: "Échouée", color: "default" },
    cancelled: { label: "Annulée", color: "default" },
    refunded: { label: "Remboursée", color: "purple" },
};

const PER_PAGE = 20;
const DATE_FORMAT = "DD/MM/YYYY HH:mm";

const StatusTag = ({ status }) => {
    const s = STATUSES[status] ?? { label: status, color: "default" };
    return <Tag color={s.color}>{s.label}</Tag>;
};

const saleDate = (order) => order.paid_at ?? order.created_at;

const Orders = () => {
    const [filters, setFilters] = useState({ status: "paid" });
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formations, setFormations] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [detail, setDetail] = useState(null);
    const [checking, setChecking] = useState(null);

    useEffect(() => {
        service({ method: "GET", url: "admin/formations" }).then((data) => setFormations(Array.isArray(data) ? data : data?.data ?? []));
        service({ method: "GET", url: "admin/formations/create" }).then((data) => setCurrencies(data.currencies ?? []));
    }, []);

    const params = useCallback(() => {
        const { range, ...rest } = filters;
        return {
            ...rest,
            q: search || undefined,
            from: range?.[0]?.format("YYYY-MM-DD"),
            to: range?.[1]?.format("YYYY-MM-DD"),
        };
    }, [filters, search]);

    const load = useCallback(() => {
        setLoading(true);
        const p = params();
        Promise.all([
            service({ method: "GET", url: "admin/orders", params: { ...p, page, per_page: PER_PAGE } }),
            service({ method: "GET", url: "admin/orders/summary", params: p }),
        ])
            .then(([list, totals]) => {
                setRows(list.data ?? []);
                setTotal(list.total ?? 0);
                setSummary(totals ?? []);
            })
            .catch(() => message.error("Impossible de charger les ventes"))
            .finally(() => setLoading(false));
    }, [params, page]);

    useEffect(() => {
        load();
    }, [load]);

    const setFilter = (key, value) => {
        setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value ?? undefined }));
    };

    const check = (order) => {
        setChecking(order.id);
        service({ method: "POST", url: `admin/orders/${order.id}/check` })
            .then((updated) => {
                if (updated.status === "paid") message.success("Paiement confirmé : l'accès à la formation a été donné.");
                else if (updated.status === "failed") message.warning("Le prestataire indique que le paiement n'a pas abouti.");
                else message.info("Toujours en attente chez le prestataire.");
                if (detail?.id === updated.id) setDetail(updated);
                load();
            })
            .catch((e) => message.error(e?.response?.data?.message ?? "Vérification impossible"))
            .finally(() => setChecking(null));
    };

    const columns = [
        {
            key: "date",
            title: "Date",
            width: 150,
            render: (_, r) => dayjs(saleDate(r)).format(DATE_FORMAT),
        },
        {
            key: "buyer",
            title: "Cliente",
            render: (_, r) => (
                <div>
                    <div className="font-medium">{r.user?.name ?? <span className="text-gray-400">Compte non créé</span>}</div>
                    <div className="text-xs text-gray-500">{r.email}</div>
                </div>
            ),
        },
        {
            key: "formation",
            title: "Formation",
            render: (_, r) =>
                r.formation ? (
                    r.formation.deleted ? (
                        <span className="text-gray-500">{r.formation.title} <Tag>Supprimée</Tag></span>
                    ) : (
                        <Link to={`/dashboard/formations/${r.formation.id}`}>{r.formation.title}</Link>
                    )
                ) : (
                    "-"
                ),
        },
        {
            key: "amount",
            title: "Montant",
            align: "right",
            render: (_, r) => <span className="font-medium whitespace-nowrap">{formatMoney(r.amount_major, r.currency)}</span>,
        },
        {
            key: "provider",
            title: "Paiement via",
            render: (_, r) => PROVIDER_LABELS[r.provider] ?? r.provider,
        },
        {
            key: "status",
            title: "Statut",
            render: (_, r) => <StatusTag status={r.status} />,
        },
        {
            key: "actions",
            width: 110,
            render: (_, r) => (
                <Space>
                    <Button type="text" icon={<EyeOutlined />} title="Détail" onClick={() => setDetail(r)} />
                    {r.status === "pending" && (
                        <Button
                            type="text"
                            icon={<SyncOutlined />}
                            title="Vérifier le paiement chez le prestataire"
                            loading={checking === r.id}
                            onClick={() => check(r)}
                        />
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            <div className="flex flex-row mt-8">
                <Breadcrumb items={[{ href: "/dashboard", title: <HomeOutlined /> }, { title: "Ventes" }]} />
                <div className="ml-auto">
                    <Button icon={<ReloadOutlined />} onClick={load}>Actualiser</Button>
                </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {summary.length ? (
                    summary.map((s) => (
                        <Card key={s.currency} size="small">
                            <div className="text-xs uppercase tracking-wide text-gray-500">Ventes payées · {s.currency}</div>
                            <div className="mt-1 text-2xl font-semibold">{formatMoney(s.total_major, s.currency)}</div>
                            <div className="text-sm text-gray-500">{s.count} vente{s.count > 1 ? "s" : ""}</div>
                        </Card>
                    ))
                ) : (
                    <Card size="small" className="sm:col-span-2">
                        <div className="text-xs uppercase tracking-wide text-gray-500">Ventes payées</div>
                        <div className="mt-1 text-gray-500">{loading ? "…" : "Aucune vente payée sur cette période."}</div>
                    </Card>
                )}
            </div>
            <div className="mt-1 text-xs text-gray-400">
                Les totaux tiennent compte des filtres, sauf du statut (seules les ventes payées sont comptées).
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
                <Select
                    value={filters.status ?? "all"}
                    onChange={(v) => setFilter("status", v)}
                    style={{ width: 160 }}
                    options={[
                        { value: "all", label: "Tous les statuts" },
                        ...Object.entries(STATUSES).map(([value, s]) => ({ value, label: s.label })),
                    ]}
                />
                <Select
                    allowClear
                    placeholder="Formation"
                    value={filters.formation_id}
                    onChange={(v) => setFilter("formation_id", v)}
                    style={{ width: 240 }}
                    options={formations.map((f) => ({ value: f.id, label: f.title }))}
                />
                <Select
                    allowClear
                    placeholder="Prestataire"
                    value={filters.provider}
                    onChange={(v) => setFilter("provider", v)}
                    style={{ width: 150 }}
                    options={Object.entries(PROVIDER_LABELS).map(([value, label]) => ({ value, label }))}
                />
                <Select
                    allowClear
                    placeholder="Devise"
                    value={filters.currency}
                    onChange={(v) => setFilter("currency", v)}
                    style={{ width: 130 }}
                    options={currencies.map((c) => ({ value: c.code, label: c.code }))}
                />
                <DatePicker.RangePicker
                    value={filters.range}
                    onChange={(v) => setFilter("range", v)}
                    format="DD/MM/YYYY"
                    placeholder={["Du", "Au"]}
                />
                <Input.Search
                    allowClear
                    placeholder="E-mail, nom ou référence"
                    onSearch={(v) => {
                        setPage(1);
                        setSearch(v.trim());
                    }}
                    style={{ width: 260 }}
                />
            </div>

            <Table
                className="mt-4"
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={rows}
                scroll={{ x: 900 }}
                locale={{ emptyText: <Empty description="Aucune vente" /> }}
                pagination={{
                    current: page,
                    pageSize: PER_PAGE,
                    total,
                    showSizeChanger: false,
                    showTotal: (t) => `${t} commande${t > 1 ? "s" : ""}`,
                    onChange: setPage,
                }}
            />

            <Drawer title="Détail de la commande" open={!!detail} onClose={() => setDetail(null)} width={520}>
                {detail && (
                    <>
                        <Descriptions bordered size="small" column={1}>
                            <Descriptions.Item label="Statut"><StatusTag status={detail.status} /></Descriptions.Item>
                            <Descriptions.Item label="Montant">{formatMoney(detail.amount_major, detail.currency)}</Descriptions.Item>
                            <Descriptions.Item label="Formation">{detail.formation?.title ?? "-"}</Descriptions.Item>
                            <Descriptions.Item label="E-mail">{detail.email}</Descriptions.Item>
                            <Descriptions.Item label="Compte">{detail.user ? `${detail.user.name} (${detail.user.email})` : "Pas encore créé (créé au paiement)"}</Descriptions.Item>
                            <Descriptions.Item label="Paiement via">{PROVIDER_LABELS[detail.provider] ?? detail.provider}</Descriptions.Item>
                            <Descriptions.Item label={`Référence ${PROVIDER_LABELS[detail.provider] ?? ""}`}>
                                {detail.provider_reference ? <Typography.Text copyable>{detail.provider_reference}</Typography.Text> : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Commande">
                                <Typography.Text copyable className="text-xs">{detail.id}</Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Créée le">{dayjs(detail.created_at).format(DATE_FORMAT)}</Descriptions.Item>
                            <Descriptions.Item label="Payée le">{detail.paid_at ? dayjs(detail.paid_at).format(DATE_FORMAT) : "-"}</Descriptions.Item>
                        </Descriptions>

                        {detail.status === "pending" && (
                            <Button className="mt-4" icon={<SyncOutlined />} loading={checking === detail.id} onClick={() => check(detail)}>
                                Vérifier le paiement chez le prestataire
                            </Button>
                        )}
                        <p className="mt-4 text-xs text-gray-500">
                            Remboursement : à faire depuis le tableau de bord du prestataire, puis retirer l'accès dans
                            l'onglet « Apprenantes » de la formation si nécessaire.
                        </p>
                    </>
                )}
            </Drawer>
        </>
    );
};

export default Orders;
